import React from 'react';
import { DonationConfiguration, ForSaleConfiguration, LineItemTypes, ShirtConfiguration } from './LineItemTypes/LineItemTypes';
import { CurrencyAmount } from '../../utils/currency';
import { GACntxt } from '../../AllianceApp';
import { LineItem } from './LineItem';
import { chooseYourOwnPriceConfiguration, fixedPriceConfiguration, LineItemUnitPriceTypes } from './UnitPriceTypes/LineItemUnitPriceTypes';
import ChooseYourOwnUnitPriceDisplay from './UnitPriceTypes/ChooseYourOwnUnitPrice';
import DonationLineItem from './LineItemTypes/DonationLineItem';
import ForSaleLineItem from './LineItemTypes/ForSaleLineItem';
import FixedUnitPriceDisplay from './UnitPriceTypes/FixedUnitPrice';
import {reserveItemFromInventory} from '../../functions/Items'
import ShirtSizeSelector from './LineItemTypes/ShirtSizeSelector';
import { updateToCart } from '../../utils/kit/modules/shoppingCart';



/**
 * Renders a display for a line item showing the (current) price and providing inputs to adjust data and other parameters.
 * A line item display typically includes a unitPriceType. Each unitPriceType has its own separate component that should be rendered.
 * The unitPriceType manages anything involving a change in price (such as quantity.)
 * A line item display typically includes a lineItemType. Each lineItemType has its own separate component that should be rendered.
 * The lineItemType manages the line item's data, which includes any information that doesn't involve price.
 * TODO: Probably need a way for these two sub-components to communicate.
 */

/**
 * Props for the LineItemDisplay
 * lineItem: The line item to display
 * onPriceChange: Callback when the price of the line item changes
 * onDataChange: Callback when the data of the line item or its validity changes
 * onPriceValidityChange: Callback when the validity of the price changes
 */
interface LineItemDisplayProps 
{
    lineItem: LineItem,
    onRemoveFromCart : (lineItemDbId: number) => void,
    onPriceChange: (lineItemId: number, newAmount:CurrencyAmount) => void,
    onDataChange : (lineItemId: number, newData: any, isValid: boolean, errorMsg?: string) => void,
    onPriceValidityChange: (lineItemId: number, isValid: boolean, errorMsg?: string) => void,
    disableInputs?: boolean
}

interface itemQuantityReserved {
    reserved_quantity: number,
    reservation_time: string
}

const LineItemDisplay = (props: LineItemDisplayProps) => 
{
    const getDefaultQuantity = () => 
    {
        if(props.lineItem.itemFormat === LineItemTypes.ForSale)
        {
            return (props.lineItem.itemConfiguration as ForSaleConfiguration).quantity ?? 0;
        }
        else if(props.lineItem.itemFormat === LineItemTypes.Shirts)
        {
            return (props.lineItem.itemConfiguration as ShirtConfiguration).totalQuantity ?? 0;
        }
        else
        {
            return 1;
        }
        
    }

    const startCollapsed = () => 
    {
        if(props.lineItem.itemFormat === LineItemTypes.ForSale)
        {
            return ((props.lineItem.itemConfiguration as ForSaleConfiguration).quantity ?? 0) === 0;
        }
        else if(props.lineItem.itemFormat === LineItemTypes.Shirts)
        {
            return ((props.lineItem.itemConfiguration as ShirtConfiguration).totalQuantity ?? 0) === 0;
        }
        else
        {
            return ((props.lineItem.unitPriceConfiguration as chooseYourOwnPriceConfiguration).defaultPrice.value.float ?? 0) === 0;
        }
        
    }

    const [priceDisplay, setPriceDisplay] = React.useState<string>(props.lineItem.defaultPriceDisplay || "$0.00");
    const [quantity, setQuantity] = React.useState<number>(getDefaultQuantity());
    const [collapsed, setCollapsed] = React.useState<boolean>(startCollapsed());

    /**
     * Handler called when the unit price type requests a change to the price to display.
     * NOTE: The displayed price can be different than the actual price reported to onPriceChange
     * (This can allow for the price to appear 'voided out' during user input without changing the total price calculation.)
     * In order to fully change the price, the unit price type must call both onPriceChange and onPriceDisplayChange.
     * @param newValue the new value to display.
     */
    const changePriceDisplay = (newValue: string) => 
    {
        setPriceDisplay(newValue);
    }

    const userContext = React.useContext(GACntxt);
    const isLoggedIn = userContext.userTGAId !== undefined;



    const onQuantityChange = async(newQuant: number) : Promise<boolean> =>
    {
        // look for how to send a request to the back end.
        // we need the item_dbid, the user_id, and the quantity
        // send the newQuant, check the return.  If 200, check the reserved count
        // if the reserved count matches the new count, all is well.
        // if the reserved count is less than the newQuant, then the user can't 
        // get more items.

        const uid = userContext.userTGAId as number;

        console.log('HIT!');

        // send request to the back end.  Get response, check to see if the requested number was reserved.
        const val = await reserveItemFromInventory(props.lineItem.itemConfiguration.dbId, uid, newQuant);

        console.log(val);


        // check the returned quantity against the new quantity.  
        // IF the numbers match, proceed with seQuantity, below        
        if (val.reserved_quantity == newQuant) {
            setQuantity(newQuant);
            console.log(val.timer_start_dt);

            updateToCart({
            ...props.lineItem,
            reservation_time: val.timer_start_dt
            });
            
            return true;
        }
        else
        {
            return false;
        }

        return false;
        // ELSE display error, move quantity back 
            // How do we display the error?
            // without seeing the new interface, hard to know!
            // grey out the button?
        // Except, in which direction?  hmmm
    }

    const onQuantityChangeShirt = (newQuant: number) =>
    {
        setQuantity(newQuant);
        const lineItemToUpdate = props.lineItem;
        lineItemToUpdate && updateToCart({
            ...lineItemToUpdate,
            itemConfiguration: {
                ...lineItemToUpdate.itemConfiguration,
                totalQuantity: newQuant
            } as ShirtConfiguration,
            data:{
                ...lineItemToUpdate.data,
                totalQuantity: newQuant
            }
        });
    }

   const onDonationPriceChange = (lineItemId: number, newAmount:CurrencyAmount) =>
   {
        updateToCart({
            ...props.lineItem,
            defaultPriceDisplay: newAmount.value.formatted,
            unitPriceConfiguration:
            {
                ...props.lineItem.unitPriceConfiguration,
                defaultPrice: newAmount
            }
        })

        props.onPriceChange(lineItemId, newAmount);
   }

    /**
     * Determines which lineItemType display to use based on the line item's configuration.
     * NOTE: This will need to be updated whenever a new lineItemType is created.
     * TODO: Is a more failure-y 500 page better suited here?
     * @param lineItem the line item to generate the display for
     * @returns render of the line item type display
     */
    const lineItemTypeDisplay = (lineItem: LineItem) => 
    {
        switch(lineItem.itemFormat)
        {
            case LineItemTypes.Donation:
                return(
                    <DonationLineItem
                        lineItemId = {props.lineItem.itemConfiguration.dbId}
                        lineItemName={props.lineItem.name}
                        itemConfiguration={lineItem.itemConfiguration as DonationConfiguration}
                        onDataChange={props.onDataChange}
                        lineItemData={props.lineItem.data}
                        disableInputs={props.disableInputs ?? false}
                    />
                )
            case LineItemTypes.ForSale:
                return(
                    <ForSaleLineItem 
                        componentName = {props.lineItem.name}
                        lineItemName = {props.lineItem.name}
                        initialData = {props.lineItem.data}
                        quantity = {quantity}
                        onQuantityChange = {onQuantityChange}
                        itemConfiguration = {lineItem.itemConfiguration as ForSaleConfiguration}
                        onDataChange={props.onDataChange}
                        disableInputs={props.disableInputs ?? false}
                        disableIncrBtn={false}
                    />
                )
            case LineItemTypes.Shirts:
                return(
                    <ShirtSizeSelector
                        shirtUnitPrice={(props.lineItem.unitPriceConfiguration as fixedPriceConfiguration).price} 
                        quantity={quantity}
                        lineItemId={props.lineItem.itemConfiguration.dbId}
                        itemConfiguration={lineItem.itemConfiguration as ShirtConfiguration}
                        onDataChange={props.onDataChange}
                        onPriceValidityChange={props.onPriceValidityChange}
                        lineItemData={props.lineItem.data}
                        disableInputs={props.disableInputs}
                        onPriceChange={props.onPriceChange}
                        onPriceDisplayChange={changePriceDisplay}
                        onQuantityChange = {onQuantityChangeShirt}
                    />
                )
            default:
                console.error('Line Item Type Not Defined');
                return(<div></div>)
        }
    }

    /**
     * Determines which unitPriceType display to use based on the line item's configuration.
     * NOTE: This will need to be updated whenever a new unitPriceType is created.
     * TODO: Is a more failure-y 500 page better suited here?
     * @param lineItem the line item to generate the display for
     * @returns render of the line item type display
     */
    const unitPriceTypeDisplay = (lineItem: LineItem) => 
    {
        switch(lineItem.unitPriceFormat)
        {
            case LineItemUnitPriceTypes.ChooseYourOwn:
                return(
                    <ChooseYourOwnUnitPriceDisplay
                        lineItemId={lineItem.itemConfiguration.dbId}
                        lineItemName={props.lineItem.name}
                        unitPriceConfiguration={lineItem.unitPriceConfiguration as chooseYourOwnPriceConfiguration}
                        onPriceChange={onDonationPriceChange}
                        onPriceValidityChange={props.onPriceValidityChange}
                        onPriceDisplayChange={changePriceDisplay}
                        disableInputs= {props.disableInputs ?? false}
                    />
                )
            case LineItemUnitPriceTypes.FixedPrice:
                return(
                    <FixedUnitPriceDisplay
                        lineItemId={lineItem.itemConfiguration.dbId}
                        unitPriceConfiguration={lineItem.unitPriceConfiguration as fixedPriceConfiguration}
                        quantity = {quantity}
                        onPriceChange={props.onPriceChange}
                        onPriceValidityChange={props.onPriceValidityChange}
                        onPriceDisplayChange={changePriceDisplay}
                    />
                )
                return(<div></div>)
            default:
                console.error('Unit Price Type Not Defined');
                return(<div></div>)
        }
    }

    const onClickRemoveItem = async (lineItem: LineItem) =>
    {
        if(lineItem.itemFormat == LineItemTypes.ForSale)
        {
            //Remove reservation
            await reserveItemFromInventory(props.lineItem.itemConfiguration.dbId, userContext.userTGAId ?? 0, 0);
        }
        props.onRemoveFromCart(lineItem.itemConfiguration.dbId);
    }

    if(collapsed)
    {
        return(
        <div className='lineItem'>
            <div className='orderItemLine'>
                <button className= "checkoutPayButton" onClick={() => setCollapsed(false)}>V</button>
                <b className="orderItemName">{props.lineItem.name}</b>
            </div>
        </div>
        )
    }
    

    //TODO: Change Class Names to not reference checkoutPage
    return(
        <div className='lineItem'>
            <div className='orderItemLine'>
                <button className= "checkoutPayButton" disabled={props.disableInputs} onClick={() => onClickRemoveItem(props.lineItem)}>X</button>
                <b className="orderItemName">{props.lineItem.name}</b>
                <div className='checkoutPageUnitPriceDisplayInput'>
                    {unitPriceTypeDisplay(props.lineItem)}
                    <b>{priceDisplay}</b>
                </div>
            </div>
            <div>
                {lineItemTypeDisplay(props.lineItem)}
            </div>
        </div>
    )
}

export default LineItemDisplay;